import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import type { ImageMetadata } from '../types/metadata.js';
import {
  DEFAULT_DPI_TOLERANCE,
  DEFAULT_DPI_WARN_TOLERANCE,
  assessDpi,
  dpiRequirementOf,
  dpiSourceLabel,
  formatDpiValue,
  metadataConfidence,
} from '../technical/index.js';
import { missingRequirementReason, notChecked, plainResult, withRequirement } from './support.js';

/**
 * DPI check: reports the resolution the *file* states.
 *
 * It is metadata reporting only: the stated resolution is never converted into
 * any other quantity, and no other quantity is derived from it. A mismatch with
 * a document's stated resolution is a warning, and only a profile that carries an
 * *official* DPI bound plus an `error` policy can ever turn it into a failure —
 * a digital photo is not rejected merely for its DPI tag.
 */
export const dpiCheck: ValidationCheck = {
  id: 'dpi',
  title: 'Разрешение (DPI)',
  description:
    'Показывает, какое разрешение записано в метаданных файла, и сравнивает его с требованием документа, если оно задано.',
  kind: 'technical',
  requires: ['metadata'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['metadata']);
    if (missing) return notChecked(ctx, dpiCheck, missing, ctx.rule.severity);

    const metadata = ctx.fileInfo.metadata as ImageMetadata;
    const dpi = metadata.dpi;
    const requirement = dpiRequirementOf(ctx.rule.params, ctx.rule.official);
    const tolerance = Math.max(0, numberParam(ctx.rule.params, 'tolerance', DEFAULT_DPI_TOLERANCE));
    const warnTolerance = Math.max(
      tolerance,
      numberParam(ctx.rule.params, 'warnTolerance', DEFAULT_DPI_WARN_TOLERANCE),
    );
    const confidence = metadataConfidence(metadata);
    const notes = [...metadata.notes];

    const details = (): Record<string, unknown> => ({
      dpi: dpi ? { x: dpi.x, y: dpi.y, source: dpi.source } : null,
      anisotropic: dpi?.anisotropic ?? false,
      requiredDpi: requirement.requiredDpi ?? null,
      source: dpi?.source ?? 'none',
      sourceLabel: dpiSourceLabel(dpi?.source ?? 'none'),
      tolerance,
      warnTolerance,
      officialRequirement: requirement.official,
      notes,
    });

    const requirementText =
      requirement.requiredDpi !== undefined
        ? `Требуемое разрешение: ${requirement.requiredDpi} DPI (±${tolerance}).`
        : undefined;

    if (!dpi) {
      notes.push('dpi-missing');
      const result = plainResult({
        id: dpiCheck.id,
        title: dpiCheck.title,
        status: 'warning',
        severity: ctx.rule.severity,
        message: 'DPI metadata отсутствует.',
        ...(requirementText ? { requirement: requirementText } : {}),
        // The DPI tag is not part of the picture; a re-export from the original
        // can write it, which is exactly what "fixable" means here.
        fixability: 'fixable',
        confidence,
        details: details(),
      });
      return withRequirement(result, ctx.rule, { unit: 'dpi' });
    }

    if (dpi.anisotropic) notes.push('dpi-anisotropic');

    const assessment = assessDpi({
      dpi,
      requirement,
      tolerance,
      warnTolerance,
      ...(ctx.rule.policy?.onViolation ? { policyOnViolation: ctx.rule.policy.onViolation } : {}),
    });
    const label = formatDpiValue(dpi);
    const effective = Math.round((dpi.x + dpi.y) / 2);

    notes.push(
      assessment.verdict === 'pass'
        ? 'dpi-ok'
        : assessment.verdict === 'fail'
          ? 'dpi-official-mismatch'
          : 'dpi-soft-mismatch',
    );

    const message = (() => {
      if (assessment.verdict === 'pass') {
        return assessment.requiredDpi !== undefined
          ? `DPI = ${label} — соответствует требованию ${assessment.requiredDpi} DPI.`
          : `DPI = ${label}.`;
      }
      if (assessment.verdict === 'fail') {
        return `DPI = ${label} не соответствует официальному требованию ${assessment.requiredDpi} DPI.`;
      }
      return `DPI = ${label}, требуемое значение — ${assessment.requiredDpi} DPI. Это предупреждение: цифровая фотография от этого не становится недействительной.`;
    })();


    const result = plainResult({
      id: dpiCheck.id,
      title: dpiCheck.title,
      status: assessment.verdict,
      severity: ctx.rule.severity,
      value: effective,
      unit: 'dpi',
      message,
      ...(requirementText ? { requirement: requirementText } : {}),
      fixability: assessment.verdict === 'pass' ? 'none' : 'fixable',
      confidence,
      details: details(),
    });
    return withRequirement(result, ctx.rule, { unit: 'dpi' });
  },
};
